import { APPSFLYER_SMART_SCRIPT } from 'apps/promo/constants/UrlConstants';
import { addScript } from 'apps/promo/helpers/DOM';

/**
 * The AppsFlyer OneLink Smart Script generates a personalized app link: the invitation code
 * is sent to deep_link_sub1 and af_sub1, and the script itself adds web attribution parameters.
 * The basic OneLink link comes from Strapi, and the parameter configuration is from the AppsFlyer dashboard.
 */
const SCRIPT_LOAD_TIMEOUT = 5000;

const MEDIA_SOURCE = 'Referral';
const XPLATFORM_VT_LOOKBACK = '72';
const SMART_SCRIPT_UI = 'true';

interface OneLinkParameterSource {
    paramKey?: string;
    defaultValue?: string;
}

interface OneLinkOptions {
    oneLinkURL: string;
    afParameters: {
        mediaSource: OneLinkParameterSource;
        afSub1?: OneLinkParameterSource;
        afCustom?: OneLinkParameterSource[];
    };
}

interface AfSmartScript {
    generateOneLinkURL: (options: OneLinkOptions) => { clickURL?: string } | null;
}

type WindowWithSmartScript = Window & { AF_SMART_SCRIPT?: AfSmartScript };

let loadPromise: Promise<AfSmartScript | null> | null = null;

const getSmartScript = (): AfSmartScript | null => {
    if (typeof window === 'undefined') return null;

    return (window as WindowWithSmartScript).AF_SMART_SCRIPT ?? null;
};

export const loadSmartScript = (): Promise<AfSmartScript | null> => {
    if (!__isBrowser__ || typeof window === 'undefined') return Promise.resolve(null);

    const loadedSmartScript = getSmartScript();
    if (loadedSmartScript) return Promise.resolve(loadedSmartScript);
    if (loadPromise) return loadPromise;

    loadPromise = new Promise((resolve) => {
        const timeoutId = setTimeout(() => {
            loadPromise = null;
            resolve(null);
        }, SCRIPT_LOAD_TIMEOUT);

        try {
            addScript(APPSFLYER_SMART_SCRIPT, () => {
                clearTimeout(timeoutId);
                resolve(getSmartScript());
            });
        } catch {
            clearTimeout(timeoutId);
            loadPromise = null;
            resolve(null);
        }
    });

    return loadPromise;
};

const buildOneLinkOptions = (oneLinkUrl: string, inviteCode: string): OneLinkOptions => ({
    oneLinkURL: oneLinkUrl,
    afParameters: {
        mediaSource: { defaultValue: MEDIA_SOURCE },
        afSub1: { defaultValue: inviteCode },
        afCustom: [
            { paramKey: 'af_xplatform_vt_lookback', defaultValue: XPLATFORM_VT_LOOKBACK },
            { paramKey: 'af_ss_ui', defaultValue: SMART_SCRIPT_UI },
            { paramKey: 'deep_link_sub1', defaultValue: inviteCode },
        ],
    },
});

interface ReferralLinkParams {
    oneLinkUrl?: string;
    inviteCode?: string;
}

interface StrapiPopupButtons {
    btn_secondary?: { url?: string };
    btn_cta?: { url?: string };
}

/** Base onelink link from Strapi */
export const resolveOneLinkUrl = (popup?: StrapiPopupButtons): string | undefined => popup?.btn_secondary?.url || popup?.btn_cta?.url;

export const generateReferralAppLink = async ({ oneLinkUrl, inviteCode }: ReferralLinkParams): Promise<string | null> => {
    if (!oneLinkUrl || !inviteCode) return null;

    const smartScript = await loadSmartScript();
    if (!smartScript) return null;

    try {
        return smartScript.generateOneLinkURL(buildOneLinkOptions(oneLinkUrl, inviteCode))?.clickURL ?? null;
    } catch {
        return null;
    }
};

/**
 * QR code link: the same personal link with the af_ss_qr tag that AppsFlyer adds
 * in its displayQrCode. We don't use displayQrCode itself—in the current version of the script (2.10.3),
 * it crashes on QRCode.CorrectLevel: within their bundle, the QRCode name is overridden by a wrapper function,
 * which doesn't have this property. We draw the QR code ourselves using qrcode.react.
 */
export const generateReferralQrLink = async (params: ReferralLinkParams): Promise<string | null> => {
    const clickURL = await generateReferralAppLink(params);

    return clickURL ? `${clickURL}&af_ss_qr=true` : null;
};
